/**
 * Runtime feature flags.
 *
 * Flags are read once at module load from Vite `import.meta.env`. Each flag has
 * an explicit default — when the env var is unset, the default applies. To turn
 * a flag on/off in a given environment, set the env var to "true"/"false"
 * (case-insensitive, also accepts "1"/"0"/"yes"/"no") in the appropriate .env
 * file or CI config.
 *
 * Production defaults are intentionally conservative: only SSO is on. Other
 * login methods are opt-in for non-prod deployments.
 */

export function parseBool(
  raw: string | undefined,
  defaultValue: boolean,
): boolean {
  if (raw === undefined) return defaultValue;
  const v = raw.trim().toLowerCase();
  if (v === "") return defaultValue;
  if (v === "true" || v === "1" || v === "yes") return true;
  if (v === "false" || v === "0" || v === "no") return false;
  // eslint-disable-next-line no-console
  console.warn(
    `[featureFlags] unrecognised boolean value "${raw}" — falling back to default (${defaultValue}).`,
  );
  return defaultValue;
}

export interface FeatureFlags {
  /** Show the Entra (Azure AD) SSO sign-in button on the login screen. */
  loginSso: boolean;
  /** Show the Pathways B2C (member portal) sign-in form on the login screen. */
  loginPathways: boolean;
  /** Show the Provider Portal B2C sign-in form on the login screen. */
  loginProviderPortal: boolean;
  /**
   * Show the legacy in-app admin console (`/admin` route + Shield nav link).
   * Default ON so nothing breaks during the migration to the standalone
   * admin-console app. Set to false per-environment once that app is live
   * there, to hide the embedded admin surface. See ACT admin-console split.
   */
  legacyAdminConsole: boolean;
  /** Enable the EHR write-back panel + write channel. Default off; Eric sets per env. */
  ehrWriteBack: boolean;
  /** Show the Ambient AI Scribe tab on member profiles (record → transcribe → note). Default off. */
  ambientScribe: boolean;
  /**
   * Draft a visit note from the open EHR chart (SOAP / Simple / E&M / MEAT) in the
   * Encounter tab, with per-section write-back. Default off — requires a
   * care-management-gql with the visit-note generation ops AND the seeded
   * ai_template rows in that environment's member_360 schema.
   *
   * @env VITE_FEATURE_AI_VISIT_NOTES
   */
  aiVisitNotes: boolean;
  /**
   * Render @astrana/ui's NATIVE (non-iframe) Create Auth module instead of the
   * PP iframe embed for the Authorizations Create/Duplicate flow. DEV/local
   * e2e flag only (Create-Auth-V2 native migration, Phase 4) — default off so
   * the iframe embed stays the shipped path everywhere until the native
   * module is deployed and vetted. Requires an @astrana/ui build that has the
   * `useNativeCreateAuth`/`authMember`/`referringProvider`/`userId`/`userEmail`
   * EmbedAuthProps (feat/create-auth-v2-native branch as of this flag's
   * introduction — not yet on any published release tag).
   */
  nativeCreateAuth: boolean;
  /**
   * Require a confirmation modal before switching to a newly-detected Elation
   * patient while viewing a matched profile. Default OFF — the default behavior
   * is to auto-navigate straight into the new patient's matching flow with no
   * modal. Set true per-environment to bring back the opt-in confirmation.
   */
  ehrSwitchConfirm: boolean;
  /**
   * Enforce server-driven persona gating for users with NO current persona
   * (ACT-1319). Default OFF → the pre-enforcement allow-all transition seam
   * stays in effect everywhere (a user with no persona sees all gated surfaces).
   * Set true per-environment (via ENV_FILE) once every real user is provisioned
   * with a persona: an unprovisioned non-admin then fails CLOSED (sees only the
   * ungated tabs). Fully reversible by flipping back. Admins with no real active
   * persona keep all-access regardless of this flag (scoped admin bypass), and
   * users WITH a real persona are unaffected in either flag state.
   */
  personaEnforcement: boolean;
  /**
   * Mount @astrana/ui's native (non-iframe) Patient Priority Index module as
   * a new top-level "Patient Panel" tab/route (P6 host wiring).
   *
   * ⚠️ **DEFAULT ON** — see the value below; the ship-gates this comment used
   * to describe are all closed. The module is published (`@astrana/ui`, pinned
   * in packages/frontend/package.json), the dev-only `@astrana/ui-ppi` source
   * alias is gone, and `pcpNpi` now has a real source: the provider enters
   * their NPI once and it is persisted per-identity (see
   * worklist/useWorklistAccessGate.ts + npiStorage.ts), with backend
   * entitlement enforced on the PP-API side.
   *
   * Defaulting ON is deliberate (demo visibility without per-env flag wiring)
   * but is still flagged `TODO(re-gate before production promotion)` at the
   * value below — `main` deploys straight to prod, so nothing but persona
   * grants gates this there. Any env can hard-hide it with
   * `VITE_FEATURE_PATIENT_PRIORITY_INDEX=false`.
   *
   * @env VITE_FEATURE_PATIENT_PRIORITY_INDEX
   */
  patientPriorityIndex: boolean;
  /**
   * Turn the standalone-web primary navigation into a STATEFUL, KEEP-ALIVE
   * multi-tab shell (P6b): Member Search, Patient Profile, and (when its own
   * flag+persona allow) Priority Worklist become three tabs that stay MOUNTED
   * across switches, preserving each view's query/results/scroll/sub-tab state
   * instead of remounting on every navigation.
   *
   * ⚠️ **DEFAULT ON** — see the value below. This comment previously said
   * "DEFAULT OFF"; that has not been true since the value was flipped, and it
   * matters because turning this on is a fundamental nav change (keeps up to
   * three views in memory at once, and moves the Patient Profile under the
   * shared tab header instead of the legacy chromeless full-bleed layout).
   *
   * ⚠️ **NOT standalone-web only.** This comment also used to claim the Chrome
   * extension "is never given the tab shell". That is wrong as of P6d:
   * `TabbedAppShell.tsx` branches on its own `EMBEDDED` constant and composes
   * its tab nav into the extension's compact `EmbeddedControls` strip, so the
   * side panel gets the keep-alive shell too. That branch is load-bearing —
   * it is why a notification click opens a member as a SEPARATE profile tab
   * rather than replacing whatever chart the clinician has open.
   *
   * When OFF, the classic route-swap navigation is unchanged.
   *
   * @env VITE_FEATURE_STATEFUL_TAB_SHELL
   */
  statefulTabShell: boolean;
  /**
   * Surface the Next Best Action feature (CM-8310 / CM-8319): the engine-picked
   * recommendation card at the top of Home, and the "Activity" tab holding the
   * member's completed-action history.
   *
   * **Default OFF, and this flag is the ONLY real off-switch.** Do not rely on
   * the persona grant to hide it — the persona gate FAILS OPEN (a user with no
   * `currentPersona` sees all gated surfaces; see useFeatureAccess.ts and the
   * `personaEnforcement` flag above), and `PERSONA_MODULES` in roles.ts is a
   * test-only parity fixture. So the gate lives at the MOUNT POINTS in
   * MemberProfilePage.tsx (`HOME_CARDS_DEFAULT` + the Activity entry in
   * `OTHER_MODULES`); with the flag off neither surface is ever mounted,
   * regardless of persona state. The FEATURE_CATALOG/PERSONA_MODULES entries
   * are inert metadata in that state.
   *
   * Turning it on requires care-management-gql's NBA work to be MERGED AND
   * DEPLOYED in that environment first — the card's "Go to <tab>" CTA calls the
   * `recordNbaDirected` mutation, which does not exist on older gql builds, and
   * the engine needs `pathways.nba_run` / `nba_completion` / `action_templates`
   * plus a working `POSTGRES_CONNECTION_STRING`. After that, flip this var in
   * the env's ENV_FILE and grant `HomeNextBestAction` / `Activity` via the
   * backend `member_360.persona_modules` table.
   *
   * @env VITE_FEATURE_NEXT_BEST_ACTION
   */
  nextBestAction: boolean;
  /**
   * Show the live translator rail (doctor↔patient speech interpretation) on
   * member profiles. Default OFF everywhere (AC-TRX-1): the rail needs two
   * server-minted vendor credentials (Vertex AI / ElevenLabs Scribe) that only
   * exist where care-management-gql is configured for them, and both vendor
   * accounts must be under a BAA before any PHI-bearing audio flows. Fails
   * closed regardless — with no credentials the rail stays read-only — but the
   * flag keeps the surface out of sight until an environment is cleared.
   */
  translator: boolean;
  /**
   * Wire the visit-note editor's `onSaveDraft` autosave seam to a server-side
   * mutation (`saveVisitNoteDraft`) instead of leaving every draft in this
   * tab's `sessionStorage` only (draft-persistence plan, A7).
   *
   * **Default OFF — two hard dependencies, neither true yet:**
   *  1. care-management-gql PR 37723 (`saveVisitNoteDraft`/`visitNoteDraft`,
   *     admin-console migration 0023 for the backing table) merged AND
   *     deployed to this environment. The host-side wiring
   *     (`members/notes/visitNoteDraftPersistence.ts`) fails soft against a
   *     missing mutation either way (falls back to the local sessionStorage
   *     draft, never throws), but there is no reason to pay a doomed network
   *     round-trip on every autosave tick while the schema doesn't have it.
   *  2. `@astrana/ui` pinned past a release carrying the `initialDraft` prop
   *     (astrana-ui PR #599, unmerged as of this flag's introduction) — the
   *     currently pinned version has no such prop, so the READ side
   *     (`useVisitNoteDraftInitial`) is built but not yet passed into
   *     `<VisitNoteDrafter>`.
   *
   * Flip on per-environment once both are true. `onSaveDraft` alone can be
   * turned on ahead of the pin bump if desired — it degrades to the local
   * fallback safely — but there is little value in doing so before the
   * schema exists.
   *
   * @env VITE_FEATURE_VISIT_NOTE_DRAFT_PERSISTENCE
   */
  visitNoteDraftPersistence: boolean;
  /**
   * Title the Clinical Summary card from the signed-in user's persona template
   * (e.g. "Pre-Round Summary") instead of the shared library's static
   * "Clinical Summary" heading.
   *
   * **Default OFF (ACT-1488 / parity item T9).** Atlas and Provider Portal both
   * show "Clinical Summary", and Copilot must match for every persona. With the
   * flag off the host passes no title at all and `@astrana/ui`'s own default
   * wins — see `members/clinicalSummaryTitle.ts` for why that is the mechanism,
   * and why no `@astrana/ui` change is needed.
   *
   * Flipping this to true restores the per-persona heading at BOTH injection
   * sites (the Home tab card and the practice-schedule row cards) — they share
   * one resolver so they can never disagree. It is a DISPLAY switch only: the
   * `member_360.ai_template` rows and the
   * `currentPersona.clinicalSummaryTemplate { title }` GraphQL selection are
   * untouched either way.
   *
   * NOTE: distinct from `VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE` (read
   * directly in `auth/CurrentUserContext.tsx`), which gates whether the
   * template field is REQUESTED from care-management-gql at all. That one must
   * also be on for this flag to have anything to show — with the field omitted
   * from the query document the persona title is always undefined.
   *
   * @env VITE_FEATURE_PERSONA_CLINICAL_SUMMARY_TITLE
   */
  personaClinicalSummaryTitle: boolean;
}

export const featureFlags: FeatureFlags = {
  loginSso: parseBool(import.meta.env.VITE_FEATURE_LOGIN_SSO, true),
  loginPathways: parseBool(import.meta.env.VITE_FEATURE_LOGIN_PATHWAYS, false),
  loginProviderPortal: parseBool(
    import.meta.env.VITE_FEATURE_LOGIN_PROVIDER_PORTAL,
    false,
  ),
  legacyAdminConsole: parseBool(
    import.meta.env.VITE_FEATURE_LEGACY_ADMIN_CONSOLE,
    true,
  ),
  ehrWriteBack: parseBool(import.meta.env.VITE_FEATURE_EHR_WRITEBACK, false),
  ambientScribe: parseBool(import.meta.env.VITE_FEATURE_AMBIENT_SCRIBE, false),
  aiVisitNotes: parseBool(import.meta.env.VITE_FEATURE_AI_VISIT_NOTES, false),
  nativeCreateAuth: parseBool(
    import.meta.env.VITE_FEATURE_NATIVE_CREATE_AUTH,
    false,
  ),
  ehrSwitchConfirm: parseBool(
    import.meta.env.VITE_FEATURE_EHR_SWITCH_CONFIRM,
    false,
  ),
  personaEnforcement: parseBool(
    import.meta.env.VITE_FEATURE_PERSONA_ENFORCEMENT,
    false,
  ),
  // Patient Priority Index worklist — default ON so it's fully visible in dev
  // for demos WITHOUT any per-env flag wiring. The `patientPriorityIndex`
  // (tab/route) and `statefulTabShell` (multi-tab keep-alive nav) pair
  // together surface the whole feature, gated by real PP-login + provider-
  // entered NPI (see useWorklistAccessGate.ts) and BE entitlement enforcement.
  // Any env can still hard-hide it by setting the VITE_FEATURE_* var to "false".
  // TODO(re-gate before production promotion).
  patientPriorityIndex: parseBool(
    import.meta.env.VITE_FEATURE_PATIENT_PRIORITY_INDEX,
    true,
  ),
  statefulTabShell: parseBool(
    import.meta.env.VITE_FEATURE_STATEFUL_TAB_SHELL,
    true,
  ),
  // Next Best Action (CM-8310 / CM-8319) — default OFF everywhere. The feature
  // ships gated so the host wiring can land on `dev` while the engine-side work
  // finishes; see the interface doc above for the flip checklist. Nothing
  // renders and no NBA mutation is reachable while this is false.
  nextBestAction: parseBool(
    import.meta.env.VITE_FEATURE_NEXT_BEST_ACTION,
    false,
  ),
  translator: parseBool(import.meta.env.VITE_FEATURE_TRANSLATOR, false),
  visitNoteDraftPersistence: parseBool(
    import.meta.env.VITE_FEATURE_VISIT_NOTE_DRAFT_PERSISTENCE,
    false,
  ),
  // ACT-1488 (T9) — default OFF so the Clinical Summary heading is the literal
  // "Clinical Summary" for every persona, matching Atlas/PP. Turning it on is
  // a deliberate, per-environment departure from parity.
  personaClinicalSummaryTitle: parseBool(
    import.meta.env.VITE_FEATURE_PERSONA_CLINICAL_SUMMARY_TITLE,
    false,
  ),
};
